import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ComplianceStatusDashboard from './components/ComplianceStatusDashboard';
import ComplianceChecklist from './components/ComplianceChecklist';
import AuditTrailVisualization from './components/AuditTrailVisualization';
import DocumentVersionControl from './components/DocumentVersionControl';
import ComplianceReportGenerator from './components/ComplianceReportGenerator';

const RegulatoryCompliance = () => {
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', label: 'Compliance Dashboard', description: 'Real-time compliance status overview' },
    { id: 'checklist', label: 'Compliance Checklist', description: 'Track regulatory requirements' },
    { id: 'audit', label: 'Audit Trail', description: 'Blockchain-verified activity tracking' },
    { id: 'documents', label: 'Document Control', description: 'Version control and verification' },
    { id: 'reports', label: 'Report Generator', description: 'Generate compliance reports' }
  ];

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <ComplianceStatusDashboard />;
      case 'checklist':
        return <ComplianceChecklist />;
      case 'audit':
        return <AuditTrailVisualization />;
      case 'documents':
        return <DocumentVersionControl />;
      case 'reports':
        return <ComplianceReportGenerator />;
      default:
        return <ComplianceStatusDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Regulatory Compliance - IFET ChainLog</title>
        <meta name="description" content="Comprehensive regulatory compliance management with blockchain verification for pharmaceutical quality assurance" />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Regulatory Compliance</h1>
                <p className="text-lg text-muted-foreground mt-2">
                  Comprehensive oversight of pharmaceutical compliance status and regulatory management
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Last Updated</div>
                  <div className="text-sm font-medium text-foreground">
                    {new Date()?.toLocaleString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex-shrink-0 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <div className="text-left">
                      <div>{tab?.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">{tab?.description}</div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Active Component */}
          <div className="animate-fade-in">
            {renderActiveComponent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegulatoryCompliance;